import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { AppIcons } from '@/components/AppIcons'
import { useTheme } from '@/hooks/useTheme'
import { currencyUtils } from '@/utils'
import type { CryptoAmount, FiatAmount } from '@/types'

const { cryptoToFiat, fiatToCrypto, parseAmountToFloat } = currencyUtils

// Данные для кнопок клавиатуры
const keypadData = [
  [
    { label: '1', value: '1' },
    { label: '2', value: '2' },
    { label: '3', value: '3' },
  ],
  [
    { label: '4', value: '4' },
    { label: '5', value: '5' },
    { label: '6', value: '6' },
  ],
  [
    { label: '7', value: '7' },
    { label: '8', value: '8' },
    { label: '9', value: '9' },
  ],
  [
    { label: '•', value: '.' },
    { label: '0', value: '0' },
    { label: 'backspace', value: 'backspace' },
  ],
]

interface NumberPadProps {
  // Режим ввода
  isFiatMode: boolean

  // Текущие суммы
  fiatAmount: FiatAmount
  cryptoAmount: CryptoAmount

  // Функции для обновления сумм
  setFiatAmount: (amount: FiatAmount) => void
  setCryptoAmount: (amount: CryptoAmount) => void

  // Выбранная валюта
  selectedCurrency?: {
    code: string
    pricePerUnit?: number
  } | null

  // Курс валют (опционально, для переопределения)
  exchangeRate?: number

  // Функция тряски при ошибке
  shakeComponent?: () => void

  // Максимальная длина ввода
  maxLength?: number

  // Блокировка нумпада во время загрузки
  disabled?: boolean
}

export const NumberPad: React.FC<NumberPadProps> = ({
  isFiatMode,
  fiatAmount,
  cryptoAmount,
  setFiatAmount,
  setCryptoAmount,
  selectedCurrency,
  exchangeRate,
  shakeComponent,
  maxLength = 12,
  disabled = false,
}) => {
  const theme = useTheme()

  // Используем переданный курс или курс из валюты
  // console.log(selectedCurrency)
  const cryptoPricePerUnit = exchangeRate || selectedCurrency?.pricePerUnit || 0

  const adaptedAmount = isFiatMode ? fiatAmount : cryptoAmount

  // WHEN THE CURRENCY ON INPUT IS FIAT
  const handleFiatInputChange = (inputAmount?: string) => {
    const fiatAmountValue = inputAmount || '0'
    setFiatAmount({
      amount: fiatAmountValue,
      code: fiatAmount.code,
    })

    const relativeInCrypto = fiatToCrypto({
      relativeFiatAmount: fiatAmountValue,
      cryptoPricePerUnitInFiat: cryptoPricePerUnit,
    })

    setCryptoAmount({
      amount: relativeInCrypto,
      code: selectedCurrency?.code || cryptoAmount.code,
    })
  }

  // WHEN THE CURRENCY ON INPUT IS CRYPTO
  const handleCryptoInputChange = (inputAmount?: string) => {
    const cryptoAmountValue = inputAmount || '0'
    const amountParsed = parseAmountToFloat({
      amount: cryptoAmountValue,
      decimalSeparator: '.',
    })

    setCryptoAmount({
      amount: cryptoAmountValue,
      code: selectedCurrency?.code || cryptoAmount.code,
    })

    const relativeInFiat = cryptoToFiat({
      amountCrypto: amountParsed,
      cryptoPricePerUnit: cryptoPricePerUnit,
    })

    setFiatAmount({
      amount: relativeInFiat,
      code: fiatAmount.code,
    })
  }

  const handleChangeAmount = (inputAmount?: string) => {
    const currencyCode = selectedCurrency?.code || cryptoAmount.code
    if (!currencyCode) {
      return
    }

    if (isFiatMode) {
      handleFiatInputChange(inputAmount)
    } else {
      handleCryptoInputChange(inputAmount)
    }
  }

  // Обработка нажатия на цифру
  const handleNumberPress = (digit: string) => {
    const currentAmount = adaptedAmount.amount

    // Проверяем максимальную длину
    if (currentAmount.length >= maxLength) {
      shakeComponent?.()
      return
    }

    // Если текущая сумма "0", заменяем на цифру
    if (currentAmount === '0') {
      handleChangeAmount(digit)
      return
    }

    // Добавляем цифру к существующей сумме
    handleChangeAmount(currentAmount + digit)
  }

  // Обработка нажатия на точку
  const handleDecimalPress = () => {
    const currentAmount = adaptedAmount.amount

    // Проверяем, есть ли уже точка
    if (currentAmount.includes('.')) {
      shakeComponent?.()
      return
    }

    // Проверяем максимальную длину
    if (currentAmount.length >= maxLength) {
      shakeComponent?.()
      return
    }

    // Если пустая строка или "0", ставим "0."
    if (!currentAmount || currentAmount === '0') {
      handleChangeAmount('0.')
      return
    }

    // Добавляем точку
    handleChangeAmount(currentAmount + '.')
  }

  // Обработка backspace
  const handleBackspace = () => {
    const currentAmount = adaptedAmount.amount

    if (currentAmount.length <= 1) {
      handleChangeAmount('0')
      return
    }

    handleChangeAmount(currentAmount.slice(0, -1))
  }

  // Обработка нажатия на кнопку
  const handleButtonPress = (value: string) => {
    if (value === 'backspace') {
      handleBackspace()
    } else if (value === '.') {
      handleDecimalPress()
    } else {
      handleNumberPress(value)
    }
  }

  return (
    <BaseView className="px-5 flex-1 flex">
      {keypadData.map((row, rowIndex) => (
        <BaseView key={rowIndex} row justify="space-between" className="flex-1">
          {row.map((button) => (
            <BaseView
              key={button.value}
              center
              className={`transition-transform duration-150 select-none flex-1 mx-2 ${
                disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer active:scale-95'
              }`}
              style={{
                paddingTop: 16,
                paddingBottom: 16,
                borderRadius: 24,
                pointerEvents: disabled ? 'none' : 'auto',
              }}
              onClick={() => !disabled && handleButtonPress(button.value)}
            >
              {button.value === 'backspace' ? (
                <AppIcons
                  name="arrowLeft"
                  size={22}
                  color={theme.colors.label.default}
                />
              ) : (
                <BaseText
                  fontSize={button.value === '0' ? 30 : 30}
                  fontFamily="medium"
                  color="label.default"
                  style={{
                    lineHeight: 1,
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {button.label}
                </BaseText>
              )}
            </BaseView>
          ))}
        </BaseView>
      ))}
    </BaseView>
  )
}
